import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  parseWorkspaceKeywordBidJob,
  parseWorkspaceKeywordBidResult,
  WORKSPACE_KEYWORD_BID_TERMINAL_STATUSES,
  type WorkspaceKeywordBidJob,
  type WorkspaceKeywordBidJobCreateBody,
  type WorkspaceKeywordBidResult,
} from '@/api/workspaceKeywordBidTypes';

const POLL_MS = 2000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollJobUntilDone(
  profileId: string,
  jobId: string,
  onUpdate: (job: WorkspaceKeywordBidJob) => void,
  abortRef: { current: boolean }
): Promise<WorkspaceKeywordBidJob> {
  for (;;) {
    const res = await adsApi.getWorkspaceKeywordBidJob(profileId, jobId);
    const job = parseWorkspaceKeywordBidJob(res.data);
    if (!job) throw new Error('Invalid bid job response');
    onUpdate(job);
    if (WORKSPACE_KEYWORD_BID_TERMINAL_STATUSES.has(job.status)) {
      return job;
    }
    if (abortRef.current) return job;
    await sleep(POLL_MS);
  }
}

export function useWorkspaceKeywordBidJob(profileId: string) {
  const queryClient = useQueryClient();
  const [job, setJob] = useState<WorkspaceKeywordBidJob | null>(null);
  const [failedResults, setFailedResults] = useState<WorkspaceKeywordBidResult[]>([]);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    return () => {
      abortRef.current = true;
    };
  }, [profileId]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['workspace-entity-page', 'keywords', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['sp-keywords'] });
    void queryClient.invalidateQueries({ queryKey: ['sb-keywords'] });
  }, [profileId, queryClient]);

  const loadFailedResults = useCallback(
    async (jobId: string) => {
      const res = await adsApi.listWorkspaceKeywordBidJobResults(profileId, jobId, {
        status: 'failed',
        limit: 100,
        offset: 0,
      });
      const raw = (res.data as { results?: unknown[] } | null)?.results ?? [];
      setFailedResults(
        raw
          .map((item) => parseWorkspaceKeywordBidResult(item))
          .filter((item): item is WorkspaceKeywordBidResult => item != null)
      );
    },
    [profileId]
  );

  const startMutation = useMutation({
    mutationFn: async (body: WorkspaceKeywordBidJobCreateBody) => {
      const createRes = await adsApi.createWorkspaceKeywordBidJob(profileId, body);
      const created = parseWorkspaceKeywordBidJob(createRes.data);
      if (!created) throw new Error('Failed to start bid change job');
      setJob(created);
      setFailedResults([]);
      if (WORKSPACE_KEYWORD_BID_TERMINAL_STATUSES.has(created.status)) {
        if ((created.totals.failed ?? 0) > 0) {
          await loadFailedResults(created.id);
        }
        return created;
      }
      const final = await pollJobUntilDone(profileId, created.id, setJob, abortRef);
      if ((final.totals.failed ?? 0) > 0) {
        await loadFailedResults(final.id);
      }
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const retryRes = await adsApi.retryWorkspaceKeywordBidJob(profileId, jobId);
      const restarted = parseWorkspaceKeywordBidJob(retryRes.data);
      if (!restarted) throw new Error('Failed to retry bid change job');
      setJob(restarted);
      setFailedResults([]);
      const final = await pollJobUntilDone(profileId, restarted.id, setJob, abortRef);
      if ((final.totals.failed ?? 0) > 0) {
        await loadFailedResults(final.id);
      }
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const isRunning =
    startMutation.isPending ||
    retryMutation.isPending ||
    (job != null && !WORKSPACE_KEYWORD_BID_TERMINAL_STATUSES.has(job.status));

  return {
    job,
    failedResults,
    isRunning,
    start: startMutation.mutateAsync,
    retryFailed: retryMutation.mutateAsync,
    reset: () => {
      setJob(null);
      setFailedResults([]);
      startMutation.reset();
      retryMutation.reset();
    },
    error: startMutation.error ?? retryMutation.error,
  };
}
